import {
  useShowVpnTools,
  useKeycloakAuth,
} from "../business-components/adminSettings/AdminContext";
import { isAppTypeIframe } from "../getAppType";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { getRedirectProps, IGetRedirectPropsResponse } from "./redirect";
import { useCurrentPath } from "./useCurrentPath";
import { useSearchParam } from "./useSearchParam";
import { CVPSearchParams, queryStringStore } from "./wsQueryStringStore";

// This is used to redirect the user to the correct page after a attach-profile token has been sent

export interface UseLoginPropsArgs {
  /** Default is currentPath */
  hasProfileRedirectUrl?: string;

  /** Default is /profile */
  noProfileRedirectUrl?: string;
}

export function useLoginProps(args: UseLoginPropsArgs = {}): LoginProps {
  const settings = useSettings();
  const currentPath = useCurrentPath();
  const showVpnTools = useShowVpnTools();
  const useKeycloak = useKeycloakAuth();

  const cvppwa = !!useSearchParam(CVPSearchParams.cvppwa);

  return getLoginProps(
    currentPath,
    settings,
    args,
    showVpnTools,
    useKeycloak ? "/auth/keycloak/login" : "/auth/visma-connect/login",
    cvppwa
  );
}

// This is used to redirect the user to the correct page after a attach-profile token has been sent
export function getLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: UseLoginPropsArgs,
  showVpnTools: boolean,
  authPath: string,
  cvppwa: boolean
): LoginProps {
  // extra step for choosing auth or skipping it
  if (showVpnTools && currentPath.replace(/^\//, "") !== "vpn-login") {
    return {
      redirectProps: getRedirectProps("vpn-login", { mergeQs: {} }),
    };
  }

  return getDirectAuthLoginProps(
    currentPath,
    settings,
    args,
    authPath,
    showVpnTools,
    cvppwa
  );
}

/** Login URL for a specific IdP (used on the VPN login chooser page). */
export function getDirectAuthLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: UseLoginPropsArgs,
  authPath: string,
  showVpnTools: boolean,
  cvppwa: boolean
): LoginProps {
  return getAuthLoginProps(
    currentPath,
    settings,
    args,
    authPath,
    showVpnTools,
    cvppwa
  );
}

/** Login URL for authskipped flow (VPN/local only). */
export function getAuthSkippedLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: UseLoginPropsArgs,
  showVpnTools: boolean,
  cvppwa: boolean
): LoginProps {
  return getAuthLoginProps(
    currentPath,
    settings,
    args,
    "/auth/authskippedlogin",
    showVpnTools,
    cvppwa
  );
}

function getAuthLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: UseLoginPropsArgs,
  authPath: string,
  showVpnTools: boolean,
  cvppwa: boolean
): LoginProps {
  let { hasProfileRedirectUrl, noProfileRedirectUrl } = getUrlsToGoToAfterLogin(
    currentPath,
    args,
    cvppwa
  );

  if (isAppTypeIframe() && settings.iframe_loginOldPortal) {
    return {
      redirectProps: {
        href: `https://secure.webtemp.no/WebtempCVPortal/default.aspx?k=${window.cvpIdConcern}`,
        target: "_blank",
      },
    };
  }

  const searchParams = new URLSearchParams();
  searchParams.set("redirectUri", hasProfileRedirectUrl);
  searchParams.set("noProfileRedirectUri", noProfileRedirectUrl);

  if (window.cvpIdConcern) {
    searchParams.set("vc_concernid", window.cvpIdConcern.toString());
    searchParams.set("k", window.cvpIdConcern.toString());
  }
  if (window.cvpEmbedType) {
    searchParams.set("vc_embedtype", window.cvpEmbedType);
  }
  if (showVpnTools) {
    searchParams.set("enableParameterOverride", "true");
  }

  searchParams.set("vc_primarycolor", settings.lt_primaryColor);

  const qs = searchParams.toString();

  return {
    redirectProps: getRedirectProps(`${location.origin}${authPath}?${qs}`, {
      mergeQs: {},
      redirectRaw: true,
    }),
  };
}

function getUrlsToGoToAfterLogin(
  currentPath: string,
  args: UseLoginPropsArgs,
  cvppwa: boolean
) {
  let {
    hasProfileRedirectUrl = currentPath,
    noProfileRedirectUrl = "/profile",
  } = args;

  hasProfileRedirectUrl = urlCorrections(hasProfileRedirectUrl);
  noProfileRedirectUrl = urlCorrections(noProfileRedirectUrl);

  const _hasProfileRedirectUrl = getRedirectProps(hasProfileRedirectUrl, {
    mergeQs: {
      k: window.cvpIdConcern ?? null,
      [CVPSearchParams.cvprb]: "true",
      [CVPSearchParams.cvppwa]: cvppwa ? "1" : null,
    },
  }).href;

  const _noProfileRedirectUrl = getRedirectProps(noProfileRedirectUrl, {
    mergeQs: {
      k: window.cvpIdConcern ?? null,
      [CVPSearchParams.cvprb]: "true",
      [CVPSearchParams.cvppwa]: cvppwa ? "1" : null,
    },
  }).href;

  return {
    hasProfileRedirectUrl: _hasProfileRedirectUrl,
    noProfileRedirectUrl: _noProfileRedirectUrl,
  };
}

function urlCorrections(url: string): string {
  // add leading slash if missing
  if (!url.startsWith("/")) {
    url = `/${url}`;
  }

  if (url === "/login") {
    // dont wanna go to this page after login
    url = "/";
  }

  if (url === "/vpn-login") {
    url = "/";
  }

  if (url === "/applicationsent") {
    // dont wanna go to this page after login
    url = "/profile";
  }

  return url;
}

export interface LoginProps {
  redirectProps: IGetRedirectPropsResponse;
}
